// 組織設定 › 電價（/org/energy；plan SPEC NRG-V6，設計稿 v04 / v04b / v04c / v04d；組織 owner / org_admin）：
//   方案清單（名稱、類型與目前版本、幣別、套用到幾個{leaf}）；〔新增方案…〕從範本或自訂；〔套用到{leaf}…〕選{leaf}（預設篩「目前用舊方案」、
//   已經是這個方案的不能勾、幣別不同不能勾）→ 1 個是第 1 級（「已套用 · 復原」）、2 個以上第 2 級確認；〔改用新版…〕加版本（生效日必填、追溯 ≤ 30 天）；
//   碳排類別與「顯示碳排卡」在同一頁，改了就存（UX-06）。
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useScope } from '../../lib/scope';
import { isOrgAdmin, orgUsable, type MyOrg } from '../../lib/planeApi';
import { Dialog } from '../../components/Dialog';
import { useConfirm } from '../../components/ConfirmDialog';
import { OrgGate, relevantOrgs } from '../shell/OrgGate';
import { energyApi, energyMode } from './api';
import { useEnergyEnv } from './hooks';
import { isFinal } from './Report';
import { energyErrText, LoadError, MockBanner, SetRow } from './parts';
import type { Tariff, TariffKind, TariffRates, TariffTemplate } from './types';
import './energy.css';

const firstOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

export function OrgEnergyPage() {
  const { t } = useTranslation();
  useEnergyEnv();
  const { orgs, homes } = useScope();
  const usable = relevantOrgs(orgs, homes).filter(orgUsable).filter(isOrgAdmin);
  return (
    <div className="page en-root km">
      <div className="en-page">
        <nav className="small muted" aria-label="breadcrumb">
          <Link to="/org">{t('energy:org.crumb')}</Link> › {t('energy:org.title')}
        </nav>
        <h1 className="page-title">{t('energy:org.title')}</h1>
        <MockBanner />
        <OrgGate candidates={usable} empty={<p className="card muted">{t('energy:org.no_admin')}</p>}>
          {(org) => <OrgTariffs key={org.org_id} org={org} />}
        </OrgGate>
      </div>
    </div>
  );
}

/** 組織設定頁上的入口卡（只有 owner / org_admin）。 */
export function OrgEnergyCard({ org }: { org: MyOrg }) {
  const { t } = useTranslation();
  useEnergyEnv();
  const admin = isOrgAdmin(org);
  const q = useQuery({ queryKey: ['energy', 'tariffs', energyMode(), org.org_id], queryFn: () => energyApi.tariffs(org.api, org.org_id), enabled: admin, retry: false, staleTime: 60_000 });
  if (!admin) return null;
  return (
    <section className="card" data-testid="en-org-card">
      <div className="toolbar">
        <h2 className="section-title" style={{ margin: 0 }}>
          {t('energy:org.card_title')}
        </h2>
        <div className="spacer" />
        <Link className="btn btn-sm" to="/org/energy">
          {t('energy:org.manage')}
        </Link>
      </div>
      {q.data && <p className="muted small" style={{ margin: 0 }}>{t('energy:org.card_hint', { n: q.data.items.length })}</p>}
    </section>
  );
}

function kindText(t: (k: string) => string, k: TariffKind) {
  return t(`energy:org.kind_${k}`);
}

function OrgTariffs({ org }: { org: MyOrg }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { homes } = useScope();
  const { ask, dialog } = useConfirm();
  const key = ['energy', 'tariffs', energyMode(), org.org_id];
  const q = useQuery({ queryKey: key, queryFn: () => energyApi.tariffs(org.api, org.org_id), retry: false });
  const [editor, setEditor] = useState(false);
  const [version, setVersion] = useState<Tariff | null>(null);
  const [apply, setApply] = useState<Tariff | null>(null);
  const [notice, setNotice] = useState<{ text: string; undo?: () => void } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: key });
    void qc.invalidateQueries({ queryKey: ['energy', 'summary'] });
  };
  const orgHomes = homes.filter((h) => h.org_id === org.org_id && !h.external);
  const homeName = (id: string) => q.data?.names?.homes?.[id]?.name ?? orgHomes.find((h) => h.home_id === id)?.display_name ?? id;
  const del = async (tr: Tariff) => {
    setErr(null);
    if (tr.applied_homes.length) return setErr(t('energy:org.in_use', { n: tr.applied_homes.length }));
    if (!(await ask({ title: t('energy:org.delete_confirm', { name: tr.name }), action: t('energy:org.delete'), danger: true }))) return;
    try {
      await energyApi.deleteTariff(org.api, org.org_id, tr.tariff_id);
      refresh();
    } catch (e) {
      setErr(energyErrText(t, e));
    }
  };
  if (q.isError) return <LoadError error={q.error} retry={() => void q.refetch()} />;
  const items = q.data?.items ?? [];
  return (
    <>
      <div className="en-head">
        <p className="en-sub" style={{ margin: 0, flex: 1 }}>
          {t('energy:org.hint')}
        </p>
        <button type="button" className="btn btn-primary" onClick={() => setEditor(true)} data-testid="en-tariff-add">
          {t('energy:org.add')}
        </button>
      </div>
      {notice && (
        <div className="km-bar ok" role="status" data-testid="en-tariff-notice">
          <span className="ic" aria-hidden>
            ✓
          </span>
          <span className="txt">{notice.text}</span>
          {notice.undo && (
            <button type="button" className="btn btn-sm" onClick={notice.undo}>
              {t('energy:apply.undo')}
            </button>
          )}
        </div>
      )}
      {err && (
        <p className="en-err" role="alert">
          {err}
        </p>
      )}
      <section className="card en-section" data-testid="en-tariffs">
        {q.isLoading ? (
          [0, 1, 2].map((i) => <div key={i} className="skel" />)
        ) : !items.length ? (
          <p className="muted">{t('energy:org.empty')}</p>
        ) : (
          <div className="table-wrap">
            <table className="km-table">
              <thead>
                <tr>
                  <th scope="col">{t('energy:org.col_name')}</th>
                  <th scope="col">{t('energy:org.col_currency')}</th>
                  <th scope="col">{t('energy:org.col_applied')}</th>
                  <th scope="col">
                    <span className="visually-hidden">{t('energy:org.col_name')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((tr) => {
                  const v = tr.versions[tr.versions.length - 1];
                  const names = tr.applied_homes.slice(0, 3).map(homeName).join('、');
                  return (
                    <tr key={tr.tariff_id} data-tariff={tr.tariff_id}>
                      <td>
                        <b>{tr.name}</b>
                        <div className="en-note">
                          {kindText(t, tr.kind)} · {t('energy:org.version_from', { v: v?.version ?? 1, date: v?.effective_from ?? '' })}
                        </div>
                        {!v?.rates && <div className="en-note">{t('energy:org.no_rates')}</div>}
                      </td>
                      <td>{tr.currency}</td>
                      <td>
                        {t('energy:org.applied_n', { n: tr.applied_homes.length })}
                        {tr.applied_homes.length > 0 && tr.applied_homes.length <= 3 && <span className="muted">（{names}）</span>}
                      </td>
                      <td>
                        <div className="toolbar wrap" style={{ justifyContent: 'flex-end' }}>
                          {v?.rates && (
                            <button type="button" className="btn btn-sm" onClick={() => setVersion(tr)}>
                              {t('energy:org.new_version')}
                            </button>
                          )}
                          <button type="button" className="btn btn-sm" onClick={() => setApply(tr)} data-testid="en-apply-open">
                            {t('energy:org.apply')}
                          </button>
                          {!tr.applied_homes.length && (
                            <button type="button" className="btn btn-sm btn-danger-outline" onClick={() => void del(tr)}>
                              {t('energy:org.delete')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <CarbonSettings org={org} />
      {editor && <TariffEditor org={org} close={() => setEditor(false)} done={() => (setEditor(false), refresh())} />}
      {version && <VersionEditor org={org} tariff={version} close={() => setVersion(null)} done={() => (setVersion(null), refresh())} />}
      {apply && (
        <ApplyFlow
          org={org}
          tariff={apply}
          all={items}
          homeName={homeName}
          close={() => setApply(null)}
          done={(ids, undo) => {
            setApply(null);
            refresh();
            setNotice({ text: ids.length === 1 ? t('energy:apply.done_one') : t('energy:apply.done', { n: ids.length }), undo });
          }}
        />
      )}
      {dialog}
    </>
  );
}

// ---- 碳排（改了就存） ----

function CarbonSettings({ org }: { org: MyOrg }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const key = ['energy', 'org-settings', energyMode(), org.org_id];
  const q = useQuery({ queryKey: key, queryFn: () => energyApi.orgSettings(org.api, org.org_id), retry: false });
  const [saved, setSaved] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: (body: { show_carbon?: boolean; carbon_category?: string }) => energyApi.putOrgSettings(org.api, org.org_id, { ...body, version: q.data?.version }),
    onSuccess: (d, body) => {
      qc.setQueryData(key, d);
      setSaved(Object.keys(body)[0]);
      void qc.invalidateQueries({ queryKey: ['energy', 'summary'] });
    },
  });
  if (!q.data) return null;
  return (
    <section className="card en-section" data-testid="en-carbon-settings">
      <h2 className="section-title">{t('energy:org.carbon_title')}</h2>
      <SetRow label={t('energy:org.carbon_category')} act={saved === 'carbon_category' ? <span className="ok">{t('energy:org.saved')}</span> : null}>
        <select className="input" value={q.data.carbon_category} onChange={(e) => m.mutate({ carbon_category: e.target.value })} aria-label={t('energy:org.carbon_category')}>
          <option value="grid">{t('energy:org.cat_grid')}</option>
        </select>
      </SetRow>
      <SetRow label={t('energy:org.show_carbon')} act={saved === 'show_carbon' ? <span className="ok">{t('energy:org.saved')}</span> : null}>
        <label className="km-check" style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
          <input type="checkbox" checked={q.data.show_carbon} onChange={(e) => m.mutate({ show_carbon: e.target.checked })} data-testid="en-show-carbon" />
          {t('energy:org.show_carbon')}
        </label>
      </SetRow>
      {m.isError && (
        <p className="en-err" role="alert">
          {energyErrText(t, m.error)}
        </p>
      )}
    </section>
  );
}

// ---- 新增方案 / 改用新版 ----

function RatesForm({ kind, currency, rates, setRates }: { kind: TariffKind; currency: string; rates: TariffRates; setRates: (r: TariffRates) => void }) {
  const { t } = useTranslation();
  const per = t('energy:ed.per_kwh', { cur: currency });
  const num = (v: number | undefined, on: (n: number) => void, label: string) => (
    <span className="en-row" style={{ gap: 6 }}>
      <input className="input en-pct" type="number" min={0} step="0.01" inputMode="decimal" value={v ?? ''} onChange={(e) => on(Number(e.target.value))} aria-label={label} style={{ textAlign: 'right' }} />
      <span className="small">{per}</span>
    </span>
  );
  if (kind === 'flat') return <SetRow label={t('energy:ed.flat_price')}>{num(rates.flat, (n) => setRates({ ...rates, flat: n }), t('energy:ed.flat_price'))}</SetRow>;
  if (kind === 'tiered')
    return (
      <>
        {(rates.tiers ?? []).map((tier, i) => (
          <SetRow key={i} label={t('energy:ed.tier', { n: i + 1 })} desc={tier.upto_kwh === null ? t('energy:ed.tier_rest') : t('energy:ed.tier_upto', { v: tier.upto_kwh })}>
            {num(tier.price, (n) => setRates({ ...rates, tiers: rates.tiers!.map((x, j) => (j === i ? { ...x, price: n } : x)) }), t('energy:ed.tier', { n: i + 1 }))}
          </SetRow>
        ))}
      </>
    );
  return (
    <>
      {(rates.seasons ?? []).flatMap((s, si) =>
        s.periods.map((p, pi) => (
          <SetRow key={`${si}-${pi}`} label={`${s.name} · ${p.name}`} desc={t('energy:ed.tou_period', { name: p.name, days: t(`energy:ed.days_${p.days}`), from: p.from_h, to: p.to_h })}>
            {num(
              p.price,
              (n) =>
                setRates({
                  ...rates,
                  seasons: rates.seasons!.map((x, i) => (i === si ? { ...x, periods: x.periods.map((y, j) => (j === pi ? { ...y, price: n } : y)) } : x)),
                }),
              `${s.name} ${p.name}`,
            )}
          </SetRow>
        )),
      )}
    </>
  );
}

function ratesOk(r: TariffRates): boolean {
  const ps = [r.flat, ...(r.tiers ?? []).map((x) => x.price), ...(r.seasons ?? []).flatMap((s) => s.periods.map((p) => p.price))].filter((x) => x !== undefined) as number[];
  return ps.length > 0 && ps.every((x) => Number.isFinite(x) && x >= 0);
}

function retroDays(date: string): number {
  return (Date.now() - Date.parse(date + 'T00:00:00')) / 86_400_000;
}

function TariffEditor({ org, close, done }: { org: MyOrg; close: () => void; done: () => void }) {
  const { t, i18n } = useTranslation();
  const tq = useQuery({ queryKey: ['energy', 'templates', energyMode(), i18n.language], queryFn: () => energyApi.templates(org.api, i18n.language), retry: false });
  const [src, setSrc] = useState<'template' | 'custom'>('template');
  const [tpl, setTpl] = useState<TariffTemplate | null>(null);
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState('TWD');
  const [rates, setRates] = useState<TariffRates>({ flat: undefined });
  const [from, setFrom] = useState(firstOfMonth());
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const kind: TariffKind = src === 'template' && tpl ? tpl.kind : 'flat';
  const pick = (id: string) => {
    const x = tq.data?.items.find((i) => i.template_id === id) ?? null;
    setTpl(x);
    if (x) {
      setName(x.name);
      setCurrency(x.currency);
      setRates(JSON.parse(JSON.stringify(x.rates)));
    }
  };
  const save = async () => {
    setErr(null);
    if (!name.trim()) return setErr(t('energy:ed.err_name'));
    if (!ratesOk(rates)) return setErr(t('energy:ed.err_price'));
    if (retroDays(from) > 30) return setErr(t('energy:ed.effective_hint'));
    setBusy(true);
    try {
      await energyApi.createTariff(org.api, org.org_id, { name: name.trim(), kind, currency, template_id: tpl?.template_id, effective_from: from, rates });
      done();
    } catch (e) {
      setErr(energyErrText(t, e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open wide title={t('energy:ed.title')} onClose={close} testId="en-tariff-editor">
      <div className="km-seg" role="group" aria-label={t('energy:ed.title')}>
        <button type="button" aria-pressed={src === 'template'} onClick={() => setSrc('template')}>
          {t('energy:ed.from_template')}
        </button>
        <button type="button" aria-pressed={src === 'custom'} onClick={() => (setSrc('custom'), setTpl(null), setRates({ flat: undefined }))}>
          {t('energy:ed.custom')}
        </button>
      </div>
      <div className="en-form">
        {src === 'template' && (
          <SetRow label={t('energy:ed.template')} desc={t('energy:ed.template_note')}>
            <select className="input" value={tpl?.template_id ?? ''} onChange={(e) => pick(e.target.value)} data-testid="en-ed-template">
              <option value="" disabled>
                —
              </option>
              {(tq.data?.items ?? []).map((x) => (
                <option key={x.template_id} value={x.template_id}>
                  {x.name}（{x.currency}）
                </option>
              ))}
            </select>
          </SetRow>
        )}
        <SetRow label={t('energy:ed.name')}>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} data-testid="en-ed-name" />
        </SetRow>
        {src === 'custom' && (
          <SetRow label={t('energy:ed.currency')}>
            <select className="input" value={currency} onChange={(e) => setCurrency(e.target.value)}>
              {['TWD', 'CNY', 'JPY', 'USD'].map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </SetRow>
        )}
        {(src === 'custom' || tpl) && <RatesForm kind={kind} currency={currency} rates={rates} setRates={setRates} />}
        <SetRow label={t('energy:ed.effective')} desc={t('energy:ed.effective_hint')}>
          <input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} required />
        </SetRow>
      </div>
      {err && (
        <p className="en-err" role="alert">
          {err}
        </p>
      )}
      <div className="dialog-actions">
        <button type="button" className="btn" onClick={close}>
          {t('energy:ed.cancel')}
        </button>
        <button type="button" className="btn btn-primary" disabled={busy || (src === 'template' && !tpl)} onClick={() => void save()} data-testid="en-ed-save">
          {t('energy:ed.save')}
        </button>
      </div>
    </Dialog>
  );
}

function VersionEditor({ org, tariff, close, done }: { org: MyOrg; tariff: Tariff; close: () => void; done: () => void }) {
  const { t } = useTranslation();
  const last = tariff.versions[tariff.versions.length - 1];
  const [rates, setRates] = useState<TariffRates>(JSON.parse(JSON.stringify(last?.rates ?? {})));
  const [from, setFrom] = useState(firstOfMonth());
  const [err, setErr] = useState<string | null>(null);
  const save = async () => {
    setErr(null);
    if (!ratesOk(rates)) return setErr(t('energy:ed.err_price'));
    try {
      await energyApi.addVersion(org.api, org.org_id, tariff.tariff_id, { effective_from: from, rates, kind: tariff.kind });
      done();
    } catch (e) {
      setErr(energyErrText(t, e));
    }
  };
  return (
    <Dialog open wide title={t('energy:ed.version_title', { name: tariff.name })} onClose={close} testId="en-version-editor">
      <div className="en-form">
        <RatesForm kind={tariff.kind} currency={tariff.currency} rates={rates} setRates={setRates} />
        <SetRow label={t('energy:ed.effective')} desc={t('energy:ed.effective_hint')}>
          <input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} required />
        </SetRow>
      </div>
      {err && (
        <p className="en-err" role="alert">
          {err}
        </p>
      )}
      <div className="dialog-actions">
        <button type="button" className="btn" onClick={close}>
          {t('energy:ed.cancel')}
        </button>
        <button type="button" className="btn btn-primary" onClick={() => void save()}>
          {t('energy:ed.version_save')}
        </button>
      </div>
    </Dialog>
  );
}

// ---- 套用到{leaf}：選{leaf} → 確認 ----

function ApplyFlow({
  org,
  tariff,
  all,
  homeName,
  close,
  done,
}: {
  org: MyOrg;
  tariff: Tariff;
  all: Tariff[];
  homeName: (id: string) => string;
  close: () => void;
  done: (ids: string[], undo?: () => void) => void;
}) {
  const { t } = useTranslation();
  const { homes } = useScope();
  const orgHomes = homes.filter((h) => h.org_id === org.org_id && !h.external);
  const currentOf = (id: string) => all.find((x) => x.applied_homes.includes(id));
  const rows = orgHomes.map((h) => {
    const cur = currentOf(h.home_id);
    const curCurrency = cur?.currency;
    const tz = h.timezone;
    // 時區推幣別：沒有方案的{leaf}看時區（中國 → CNY）；有方案看方案
    const cny = curCurrency ? curCurrency !== tariff.currency : (tz === 'Asia/Shanghai') !== (tariff.currency === 'CNY');
    return { id: h.home_id, name: homeName(h.home_id), cur, already: cur?.tariff_id === tariff.tariff_id, currencyDiff: cny ? (curCurrency ?? (tz === 'Asia/Shanghai' ? 'CNY' : 'TWD')) : null };
  });
  const oldCount = rows.filter((r) => r.cur && !r.already && !r.currencyDiff).length;
  const noneCount = rows.filter((r) => !r.cur && !r.currencyDiff).length;
  const [filter, setFilter] = useState<'old' | 'none' | 'all'>(oldCount ? 'old' : noneCount ? 'none' : 'all');
  const visibleRows = rows.filter((r) => (filter === 'old' ? r.cur && !r.already && !r.currencyDiff : filter === 'none' ? !r.cur && !r.currencyDiff : true));
  const [sel, setSel] = useState<Set<string>>(() => new Set(visibleRows.filter((r) => !r.already && !r.currencyDiff).map((r) => r.id)));
  const [step, setStep] = useState<'pick' | 'confirm'>('pick');
  const [from, setFrom] = useState(firstOfMonth());
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const picked = rows.filter((r) => sel.has(r.id));
  const prevByOld = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of picked) if (r.cur) m.set(r.cur.name, (m.get(r.cur.name) ?? 0) + 1);
    return [...m.entries()];
  }, [picked]);
  const doApply = async () => {
    setErr(null);
    if (retroDays(from) > 30) return setErr(t('energy:ed.effective_hint'));
    setBusy(true);
    const ids = picked.map((r) => r.id);
    const before = picked.map((r) => ({ id: r.id, tariff: r.cur?.tariff_id }));
    try {
      await energyApi.applyTariff(org.api, org.org_id, tariff.tariff_id, { home_ids: ids, effective_from: from });
      const undo =
        ids.length === 1 && before[0].tariff
          ? () => void energyApi.applyTariff(org.api, org.org_id, before[0].tariff!, { home_ids: ids, effective_from: from }).then(() => done([]))
          : undefined;
      done(ids, undo);
    } catch (e) {
      setErr(energyErrText(t, e));
    } finally {
      setBusy(false);
    }
  };
  // 1 個是第 1 級（直接套用 + 復原）、2 個以上第 2 級確認
  const next = () => (picked.length === 1 ? void doApply() : setStep('confirm'));
  if (step === 'confirm')
    return (
      <Dialog open title={t('energy:apply.confirm_title', { name: tariff.name, n: picked.length })} onClose={close} testId="en-apply-confirm">
        <div data-testid="confirm-dialog" data-level={2}>
          <p>{t('energy:apply.confirm_body', { n: picked.length, date: from })}</p>
          {prevByOld.map(([old, n]) => (
            <p key={old}>{t('energy:apply.confirm_old', { n, old })}</p>
          ))}
          <SetRow label={t('energy:ed.effective')} desc={t('energy:ed.effective_hint')}>
            <input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} required />
          </SetRow>
          {isFinal(from.slice(0, 7)) && <p className="en-note">{t('energy:apply.final_warn', { month: from.slice(0, 7) })}</p>}
          {err && (
            <p className="en-err" role="alert">
              {err}
            </p>
          )}
          <div className="dialog-actions">
            <button type="button" className="btn" onClick={() => setStep('pick')}>
              {t('energy:ed.cancel')}
            </button>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void doApply()} data-testid="en-apply-ok">
              {t('energy:apply.ok')}
            </button>
          </div>
        </div>
      </Dialog>
    );
  return (
    <Dialog open wide title={t('energy:apply.title', { name: tariff.name })} onClose={close} testId="en-apply-pick">
      <div className="en-row" role="group" aria-label={t('energy:apply.title', { name: tariff.name })}>
        {oldCount > 0 && (
          <button type="button" className={`km-chip${filter === 'old' ? ' on' : ''}`} aria-pressed={filter === 'old'} onClick={() => setFilter('old')}>
            {t('energy:apply.filter_old', { n: oldCount })}
          </button>
        )}
        {noneCount > 0 && (
          <button type="button" className={`km-chip${filter === 'none' ? ' on' : ''}`} aria-pressed={filter === 'none'} onClick={() => setFilter('none')}>
            {t('energy:apply.filter_none', { n: noneCount })}
          </button>
        )}
        <button type="button" className={`km-chip${filter === 'all' ? ' on' : ''}`} aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>
          {t('energy:apply.filter_all', { n: rows.length })}
        </button>
      </div>
      <p className="small" role="status">
        {t('energy:apply.shown', { n: visibleRows.length })}
        {filter !== 'all' && rows.length > visibleRows.length && (
          <>
            {' · '}
            {t('energy:apply.hidden', { n: rows.length - visibleRows.length })}{' '}
            <button type="button" className="link-btn" onClick={() => setFilter('all')}>
              {t('energy:apply.clear_filter')}
            </button>
          </>
        )}
      </p>
      <ul className="en-pick" style={{ maxHeight: 420, overflow: 'auto' }} data-testid="en-apply-list">
        {visibleRows.map((r) => {
          const dis = r.already || !!r.currencyDiff;
          return (
            <li key={r.id} className={dis ? 'dis' : undefined} data-home={r.id}>
              <input
                type="checkbox"
                id={`ap-${r.id}`}
                checked={sel.has(r.id)}
                disabled={dis}
                onChange={() => setSel((s) => (s.has(r.id) ? new Set([...s].filter((x) => x !== r.id)) : new Set([...s, r.id])))}
              />
              <label htmlFor={`ap-${r.id}`}>{r.name}</label>
              <span className="cur small muted">
                {r.already
                  ? t('energy:apply.already')
                  : r.currencyDiff
                    ? t('energy:apply.currency_diff', { cur: r.currencyDiff })
                    : r.cur
                      ? t('energy:apply.current', { name: r.cur.name })
                      : t('energy:apply.current_none')}
              </span>
            </li>
          );
        })}
      </ul>
      {err && (
        <p className="en-err" role="alert">
          {err}
        </p>
      )}
      <div className="dialog-actions">
        <button type="button" className="btn" onClick={close}>
          {t('energy:ed.cancel')}
        </button>
        <button type="button" className="btn btn-primary" disabled={!picked.length || busy} onClick={next} data-testid="en-apply-next">
          {picked.length ? t('energy:apply.next', { n: picked.length }) : t('energy:apply.none_picked')}
        </button>
      </div>
    </Dialog>
  );
}
